import * as z from "zod";
import { isoDate } from "./common";

/**
 * The platform back-office's view of an event: read from a third-party calendar
 * by the import, or added here. Times are instants; `timezone` is the zone a
 * show's own dates and times are read in.
 */

export const ADMIN_EVENT_SOURCES = ["MERCHANT", "IMPORTED"] as const;

export const AdminEventPlace = z.object({
  id: z.string(),
  name: z.string().nullable(),
  city: z.string().nullable(),
  /** Two letters, read from `province` whichever spelling it holds; null outside the US. */
  stateCode: z.string().nullable(),
  province: z.string().nullable(),
  countryCode: z.string().nullable(),
});
export type AdminEventPlace = z.output<typeof AdminEventPlace>;

const eventFields = {
  id: z.string(),
  title: z.string(),
  startsAt: isoDate,
  endsAt: isoDate,
  /** The event's zone, else its venue's; null when neither has one. */
  timezone: z.string().nullable(),
  /** IMPORTED: read by the event import. MERCHANT: added here. */
  source: z.enum(ADMIN_EVENT_SOURCES),
  externalSource: z.string().nullable(),
  isPublished: z.boolean(),
  isBlocked: z.boolean(),
  isDeleted: z.boolean(),
  /** When its source stopped listing it while it was still upcoming. */
  delistedAt: isoDate.nullable(),
  /** Set on a copy: the listing the calendar shows instead. */
  canonicalEventId: z.string().nullable(),
  /** Copies of this show from other sources, hidden behind it. */
  copies: z.number(),
};

export const AdminEvent = z.object({ ...eventFields, place: AdminEventPlace });
export type AdminEvent = z.output<typeof AdminEvent>;

export const AdminEventList = z.object({
  events: z.array(AdminEvent),
  /** Events matching the filter — what pages the table. */
  total: z.number(),
  /** State codes of the venues matching every filter but the state one: the state filter's choices. */
  states: z.array(z.string()),
});
export type AdminEventList = z.output<typeof AdminEventList>;

export const AdminEventPlaceDetail = AdminEventPlace.extend({
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  timezone: z.string().nullable(),
  source: z.enum(ADMIN_EVENT_SOURCES),
});
export type AdminEventPlaceDetail = z.output<typeof AdminEventPlaceDetail>;

export const AdminEventOrganizer = z.object({
  id: z.string(),
  name: z.string().nullable(),
});
export type AdminEventOrganizer = z.output<typeof AdminEventOrganizer>;

/** Another listing of the same show, as the duplicate grouping linked them. */
export const AdminEventCopy = z.object({
  id: z.string(),
  title: z.string(),
  externalSource: z.string().nullable(),
  startsAt: isoDate,
});
export type AdminEventCopy = z.output<typeof AdminEventCopy>;

export const AdminEventDetail = z.object({
  ...eventFields,
  place: AdminEventPlaceDetail,
  description: z.string().nullable(),
  externalId: z.string().nullable(),
  shareUrl: z.string().nullable(),
  slug: z.string().nullable(),
  flyerUrl: z.string().nullable(),
  organizer: AdminEventOrganizer.nullable(),
  /** On a copy: the listing shown instead. */
  canonical: AdminEventCopy.nullable(),
  /** On the listing shown: its copies from other sources. */
  duplicates: z.array(AdminEventCopy),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type AdminEventDetail = z.output<typeof AdminEventDetail>;

/**
 * Every field optional. By default: upcoming shows, copies hidden, deleted rows
 * left out. Drafts, blocked and delisted rows stay in.
 */
export interface AdminEventQuery {
  /**
   * A show is upcoming until its end has passed; `past` lists the latest first.
   * Unless given, `upcoming`, or `all` when `from` or `to` is: a window says when by itself.
   */
  when?: "upcoming" | "past" | "all";
  /** Only shows still going at or after this instant. */
  from?: Date;
  /** Only shows starting before this instant. */
  to?: Date;
  /** A US state's code or name. */
  state?: string;
  /** `added`: not from the import, but made here. */
  source?: "all" | "imported" | "added";
  /** `all` still leaves deleted rows out; `deleted` is the only way to see them. */
  status?: "all" | "published" | "draft" | "blocked" | "delisted" | "deleted";
  /**
   * Include the copies the calendar hides behind another listing. Each import run links copies among upcoming
   * shows only, so after the first run past a show's end, each source's listing of it is a row of its own either way.
   */
  copies?: boolean;
  /** Matches the title, the venue's name or town, or an exact event id or source id. */
  search?: string;
  limit?: number;
  offset?: number;
  /**
   * Default true. False leaves `states` empty and skips the query behind it: for the pages after the first of one
   * filter, which have them already.
   */
  withStates?: boolean;
}
